import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import '../styles/AdminMessages.css';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [replyText, setReplyText] = useState('');
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [sending, setSending] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await api.get('/contact/messages');
      setMessages(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await api.put(`/contact/messages/${id}/read`);
      setMessages(messages.map(m => m.id === id ? { ...m, status: 'read' } : m));
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleMarkAsReplied = async (id) => {
    try {
      await api.put(`/contact/messages/${id}/replied`);
      setMessages(messages.map(m => m.id === id ? { ...m, status: 'replied' } : m));
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirms.deleteMessage'))) return;
    try {
      await api.delete(`/contact/messages/${id}`);
      setMessages(messages.filter(m => m.id !== id));
      if (selectedMessage?.id === id) setSelectedMessage(null);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;

    setSending(true);
    try {
      await api.post(`/contact/messages/${selectedMessage.id}/reply`, { reply: replyText });
      setMessages(messages.map(m =>
        m.id === selectedMessage.id
          ? { ...m, status: 'replied', reply: replyText, replied_at: new Date().toISOString() }
          : m
      ));
      setSelectedMessage(prev => ({ ...prev, status: 'replied', reply: replyText, replied_at: new Date().toISOString() }));
      setReplyText('');
      setShowReplyForm(false);
      alert(t('admin.messages.replySuccess'));
    } catch (error) {
      console.error('Erreur:', error);
      alert(t('admin.messages.replyError'));
    } finally {
      setSending(false);
    }
  };

  const formatDate = (date) => {
    return formatDateTime(date);
  };

  const filteredMessages = messages.filter(m => {
    if (filter === 'all') return true;
    return m.status === filter;
  });

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  if (loading) {
    return <div className="admin-messages loading">{t('common.loading')}</div>;
  }

  return (
    <div className="admin-messages">
      <div className="messages-header">
        <h1>{t('admin.messages.title')}</h1>
        {unreadCount > 0 && (
          <span className="unread-badge">{unreadCount} {t('admin.messages.unread', { plural: unreadCount > 1 ? 's' : '' })}</span>
        )}
      </div>

      <div className="messages-filters">
        <button
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          {t('admin.messages.all', { count: messages.length })}
        </button>
        <button
          className={filter === 'unread' ? 'active' : ''}
          onClick={() => setFilter('unread')}
        >
          {t('admin.messages.unreadFilter', { count: messages.filter(m => m.status === 'unread').length })}
        </button>
        <button
          className={filter === 'read' ? 'active' : ''}
          onClick={() => setFilter('read')}
        >
          {t('admin.messages.readFilter', { count: messages.filter(m => m.status === 'read').length })}
        </button>
        <button
          className={filter === 'replied' ? 'active' : ''}
          onClick={() => setFilter('replied')}
        >
          {t('admin.messages.repliedFilter', { count: messages.filter(m => m.status === 'replied').length })}
        </button>
      </div>

      <div className="messages-container">
        <div className="messages-list">
          {filteredMessages.length === 0 ? (
            <p className="no-messages">{t('admin.messages.noMessages')}</p>
          ) : (
            filteredMessages.map(message => (
              <div
                key={message.id}
                className={`message-item ${message.status} ${selectedMessage?.id === message.id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedMessage(message);
                  if (message.status === 'unread') {
                    handleMarkAsRead(message.id);
                  }
                }}
              >
                <div className="message-item-header">
                  <span className="message-name">{message.name}</span>
                  <span className={`message-status status-${message.status}`}>
                    {message.status === 'unread' ? t('admin.messages.newBadge') : message.status === 'read' ? t('admin.messages.readBadge') : t('admin.messages.repliedBadge')}
                  </span>
                </div>
                <div className="message-subject">{message.subject}</div>
                <div className="message-date">{formatDate(message.created_at)}</div>
              </div>
            ))
          )}
        </div>

        <div className="message-detail">
          {selectedMessage ? (
            <>
              <div className="detail-header">
                <h2>{selectedMessage.subject}</h2>
                <div className="detail-actions">
                  {selectedMessage.status !== 'replied' && (
                    <button
                      className="btn-replied"
                      onClick={() => handleMarkAsReplied(selectedMessage.id)}
                    >
                      {t('admin.messages.markReplied')}
                    </button>
                  )}
                  <button
                    className="btn-delete"
                    onClick={() => handleDelete(selectedMessage.id)}
                  >
                    {t('common.delete')}
                  </button>
                </div>
              </div>
              <div className="detail-info">
                <p><strong>{t('admin.messages.from')}</strong> {selectedMessage.name} ({selectedMessage.email})</p>
                {selectedMessage.username && (
                  <p><strong>{t('admin.messages.user')}</strong> {selectedMessage.username}</p>
                )}
                <p><strong>{t('admin.messages.dateLabel')}</strong> {formatDate(selectedMessage.created_at)}</p>
              </div>
              <div className="detail-message">
                {selectedMessage.message}
              </div>

              {/* Afficher la reponse precedente si existe */}
              {selectedMessage.reply && (
                <div className="previous-reply">
                  <h4>{t('admin.messages.yourReply')}</h4>
                  <div className="reply-content">{selectedMessage.reply}</div>
                  <div className="reply-date">{t('admin.messages.repliedAt', { date: formatDate(selectedMessage.replied_at) })}</div>
                </div>
              )}

              {/* Formulaire de reponse */}
              <div className="detail-reply">
                {!showReplyForm ? (
                  <button
                    className="btn-reply"
                    onClick={() => setShowReplyForm(true)}
                  >
                    {selectedMessage.reply ? t('admin.messages.newReply') : t('admin.messages.reply')}
                  </button>
                ) : (
                  <div className="reply-form">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={t('admin.messages.replyPlaceholder')}
                      rows="5"
                      disabled={sending}
                    />
                    <div className="reply-actions">
                      <button
                        className="btn-send"
                        onClick={handleSendReply}
                        disabled={sending || !replyText.trim()}
                      >
                        {sending ? t('common.sending') : t('admin.messages.sendReply')}
                      </button>
                      <button
                        className="btn-cancel"
                        onClick={() => {
                          setShowReplyForm(false);
                          setReplyText('');
                        }}
                        disabled={sending}
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <p>{t('admin.messages.selectMessage')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
